// ============================================================
// TMR Portal Admin — campo de escolha com busca sobre uma lista já carregada
// ============================================================
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Icons, StrokeIcon } from '../lib/icons';
import { Hint } from './Tooltip';

/** One option of a {@link ComboField}. */
export interface ComboOption {
  /** What the form stores — the code, the id, never the label. */
  value: string;
  /** The line the operator reads and searches. */
  label: string;
  /** A second, muted line, also searched. Use it for the fuller name behind an abbreviation. */
  secondary?: string;
}

/** Props for {@link ComboField}. */
export interface ComboFieldProps {
  label?: string;
  /** Explains the field in a tooltip, revealed by the label's dashed underline. */
  hint?: ReactNode;
  required?: boolean;
  /** The selected option's `value`, or empty. */
  value: string;
  onChange: (value: string) => void;
  options: ComboOption[];
  placeholder?: string;
  error?: string | null | false;
  disabled?: boolean;
  /** Said when the term matches nothing. */
  notFoundMessage?: string;
}

/** How many matches to render. Past this, the term is not narrow enough for a list to help. */
const MAX_VISIBLE = 50;

/**
 * Picks one option from a list the caller already has, filtering as the operator types.
 *
 * It exists because a native `<select>` can only be searched by the **beginning of its label**: with
 * options written "208 · BTG Pactual", typing "BTG" finds nothing and only "208" jumps — which is exactly
 * backwards for somebody who knows their bank by name and not by its COMPE code.
 *
 * Deliberately client-side. It is for a list that is already in hand and small enough to hold — a few
 * hundred banks, cached for an hour — where a round-trip per keystroke would be slower and could fail. For
 * a directory too large to load, the answer is {@link RecordPicker}, which searches on the server.
 *
 * The match ignores case and accents, so "sao" finds "São" — the alternative is an operator typing a name
 * correctly and being told it does not exist.
 */
export function ComboField({
  label,
  hint,
  required,
  value,
  onChange,
  options,
  placeholder,
  error,
  disabled,
  notFoundMessage = 'Nada encontrado.',
}: ComboFieldProps) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [active, setActive] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const selected = options.find((option) => option.value === value) ?? null;

  const matches = useMemo(() => filterComboOptions(options, term), [options, term]);

  // Closing on an outside click, not on blur: blur fires before the option's own click handler, so
  // closing there would swallow the very pick the operator just made.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    setTerm('');
    setActive(0);
  };

  const pick = (option: ComboOption) => {
    onChange(option.value);
    close();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (!open && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => Math.min(current + 1, matches.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = matches[active];
      if (option) pick(option);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {label && (
        <label className="pa-form-label">
          {hint ? <Hint tip={hint}>{label}</Hint> : label}
          {required && <span style={{ color: 'var(--danger)' }}> *</span>}
        </label>
      )}

      <div style={{ position: 'relative' }}>
        <input
          // When closed it shows what is chosen; typing turns it into the search term. One box, so the
          // operator never has to find a separate place to type.
          value={open ? term : (selected?.label ?? '')}
          placeholder={selected ? selected.label : placeholder}
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          onChange={(event) => {
            setTerm(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className="pa-input"
          style={{
            width: '100%',
            padding: '9px 34px 9px 12px',
            fontFamily: 'var(--font-sans)',
            fontSize: 14,
            border: `1px solid ${error ? 'var(--danger)' : 'var(--border-default)'}`,
            borderRadius: 'var(--radius-sm)',
            background: disabled ? 'var(--cream-100)' : 'var(--cream-50)',
            color: 'var(--ink-900)',
            outline: 'none',
          }}
        />
        <span
          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--stone-400)', pointerEvents: 'none', display: 'flex' }}
        >
          <StrokeIcon d={Icons.chevronD} size={15} />
        </span>
      </div>

      {open && !disabled && (
        <ul
          id={listId}
          role="listbox"
          style={{
            position: 'absolute',
            zIndex: 20,
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            maxHeight: 260,
            overflowY: 'auto',
            margin: 0,
            padding: 4,
            listStyle: 'none',
            background: 'var(--cream-50)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {matches.length === 0 && (
            <li style={{ padding: '10px 12px', fontSize: 13, color: 'var(--fg-muted)' }}>{notFoundMessage}</li>
          )}
          {matches.map((option, index) => (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                onMouseEnter={() => setActive(index)}
                onClick={() => pick(option)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  border: 0,
                  cursor: 'pointer',
                  padding: '8px 10px',
                  borderRadius: 'var(--radius-sm)',
                  background: index === active ? 'var(--sage-100)' : 'transparent',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 13.5,
                  color: 'var(--ink-900)',
                }}
              >
                <div>{option.label}</div>
                {option.secondary && (
                  <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>{option.secondary}</div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 5 }}>{error}</div>}
    </div>
  );
}

/**
 * The options a term matches, capped at what a list can usefully show.
 *
 * Exported and pure because it is the part of this component that would regress in silence: a matcher that
 * quietly stopped ignoring accents, or stopped searching the secondary line, still renders a perfectly
 * normal dropdown — it just never finds the bank somebody typed the name of.
 *
 * An empty term is not "no matches": it is "nothing typed yet", so the head of the list is shown.
 *
 * @param options The full list.
 * @param term What the operator typed.
 * @param limit Maximum matches to return.
 */
export function filterComboOptions(
  options: ComboOption[],
  term: string,
  limit: number = MAX_VISIBLE,
): ComboOption[] {
  const needle = normalize(term);
  if (!needle) return options.slice(0, limit);

  return options
    .filter((option) =>
      normalize(option.label).includes(needle) || normalize(option.secondary ?? '').includes(needle))
    .slice(0, limit);
}

/** Lower-cased and stripped of accents, so "sao" matches "São" and "BTG" matches "btg". */
function normalize(value: string): string {
  return value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();
}
